import React, { createContext, useContext, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";

const GlobalSnackbarContext = createContext(null);

export function GlobalSnackbarProvider({ children }) {
  const [snack, setSnack] = useState({
    open: false,
    message: "",
    duration: 2000,
  });
  // Separate state for success-variant snacks so they don't fight the
  // default snack on anchor/style. Rendered at bottom-center as a green
  // MUI Alert, matching the mobile post-job-form confirmation style.
  const [successSnack, setSuccessSnack] = useState({
    open: false,
    message: "",
    duration: 2000,
  });

  const handleSnack = useCallback((message, duration) => {
    setSnack({ open: true, message, duration: duration || 2000 });
  }, []);

  const handleSuccessSnack = useCallback((message, duration) => {
    setSuccessSnack({ open: true, message, duration: duration || 2000 });
  }, []);

  const handleClose = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setSnack((prev) => ({ ...prev, open: false }));
  }, []);

  const handleSuccessClose = useCallback((event, reason) => {
    if (reason === "clickaway") return;
    setSuccessSnack((prev) => ({ ...prev, open: false }));
  }, []);

  return (
    <GlobalSnackbarContext.Provider
      value={{ handleSnack, handleSuccessSnack, handleClose }}
    >
      {children}
      {createPortal(
        <>
          <Snackbar
            open={snack.open}
            autoHideDuration={snack.duration}
            onClose={handleClose}
            message={snack.message}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
            // Above every modal layer so a live-update toast is never hidden
            // behind an open dialog (GlobalConfirm 1000002, CycleConfirmationModal
            // 1000003 are the current ceiling). Portalled to <body> so no
            // ancestor stacking context can trap it.
            sx={{ zIndex: 1100000 }}
          />
          <Snackbar
            open={successSnack.open}
            autoHideDuration={successSnack.duration}
            onClose={handleSuccessClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            sx={{ zIndex: 1100001 }}
          >
            <Alert
              onClose={handleSuccessClose}
              severity="success"
              variant="filled"
              sx={{ width: "100%", fontWeight: 600 }}
            >
              {successSnack.message}
            </Alert>
          </Snackbar>
        </>,
        document.body
      )}
    </GlobalSnackbarContext.Provider>
  );
}

export function useSnackbar() {
  const context = useContext(GlobalSnackbarContext);
  if (!context) {
    throw new Error("useSnackbar must be used within GlobalSnackbarProvider");
  }
  return context;
}
